import { Plus } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useSegments } from "@/hooks/useSegments";
import { DeleteSegmentDialog } from "@/components/spy-dashboard/segments/DeleteSegmentDialog";
import { SegmentForm } from "@/components/spy-dashboard/segments/SegmentForm";
import { SegmentList } from "@/components/spy-dashboard/segments/SegmentList";
import {
  emptySegmentFormValues,
  formValuesToPayload,
  segmentToFormValues,
  type SegmentFormValues,
} from "@/components/spy-dashboard/segments/segmentFormSchema";

export default function SegmentsTab() {
  const {
    segments,
    isLoading,
    isFormOpen,
    isCreating,
    editingSegment,
    segmentToDelete,
    saveSegment,
    deleteSegment,
    startCreate,
    startEdit,
    cancelForm,
    requestDelete,
    cancelDelete,
    confirmDelete,
  } = useSegments();

  const handleSave = (values: SegmentFormValues) => {
    saveSegment.mutate(
      {
        id: editingSegment?.id,
        payload: formValuesToPayload(values),
      },
      {
        onSuccess: cancelForm,
        onError: (error) => {
          console.error("Error saving segment:", error);
          toast.error("Error saving segment!");
        },
      },
    );
  };

  if (isFormOpen) {
    const formKey = isCreating ? "create" : editingSegment!.id;
    const initialValues = isCreating
      ? emptySegmentFormValues
      : segmentToFormValues(editingSegment!);

    return (
      <SegmentForm
        key={formKey}
        mode={isCreating ? "create" : "edit"}
        initialValues={initialValues}
        isSaving={saveSegment.isPending}
        onBack={cancelForm}
        onSave={handleSave}
      />
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold flex items-center gap-2">
          🧩 Manage Segments
        </h2>
        <Button onClick={startCreate} className="gap-2 cursor-pointer">
          <Plus className="w-4 h-4" /> Create Segment
        </Button>
      </div>

      <SegmentList
        segments={segments}
        isLoading={isLoading}
        onEdit={startEdit}
        onDelete={requestDelete}
      />

      <DeleteSegmentDialog
        open={segmentToDelete !== null}
        isDeleting={deleteSegment.isPending}
        onOpenChange={(open) => {
          if (!open) cancelDelete();
        }}
        onConfirm={confirmDelete}
      />
    </div>
  );
}
